/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import i18n from 'i18next';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { FormHandles } from '@unform/core';

import { useSnackbar } from 'notistack';

import { Fade } from '@material-ui/core';

import { IProfilePermission, ItemList } from '../../../utils/interfaces';

import FixedHeader from '../../../components/Tools/Breadcrumb';
import LoaderPda from '../../../components/Tools/LoaderPda';
import SearchBox from '../../../components/Tools/Search';

import { PageContainer, Search } from '../../../app/styles';

import ProfilePermissionsTable from './components/ProfilePermissionsTable';

import { searchBoxRequest, getOptionsAPIs } from './api/profilePermissions.api';

import { ISearchParam } from './types';

const ProfilePermissions: React.FC = () => {
  const searchRef = useRef<FormHandles>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [disableInput, setDisableInput] = useState(false);
  const [isShowingTable, setIsShowingTable] = useState(false);

  const [systems, setSystems] = useState<ItemList[]>([]);
  const [clients, setClients] = useState<ItemList[]>([]);

  const [paramsData, setParamsData] = useState<ISearchParam>(
    {} as ISearchParam
  );

  const [profilePermissions, setProfilePermissions] = useState<
    IProfilePermission[]
  >([]);

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const traductorMsg = (msg: string) => {
    return i18n.t(msg);
  };
  const { profileCode } = useParams<Record<string, string | undefined>>();
  const clientId = Number(localStorage.getItem('@pdamodules::codigoCliente'));

  const codigoPerfil = String(profileCode);

  const getSystemSelected = (systemId?: number) => {
    const isNotAdminProfile = String(clientId) !== '0';

    if (!systemId) {
      setDisableInput(false);

      searchRef.current?.setFieldValue('client', '');

      return;
    }

    const isAdmin =
      systems.find(s => s.value === systemId)?.label === 'Administração';

    if (isAdmin && !isNotAdminProfile) {
      searchRef.current?.setFieldValue('client', '');
    }

    setDisableInput(isAdmin || isNotAdminProfile);
  };

  const getOptions = useCallback(async () => {
    setIsLoading(true);

    await getOptionsAPIs(clientId, codigoPerfil)
      .then(res => {
        const systemsOptions = res[0];
        const clientsOptions = res[1];

        setSystems(systemsOptions);

        setClients(clientsOptions);
      })
      .finally(() => setIsLoading(false));
  }, [clientId, codigoPerfil]);

  const handleGetSystemSelected = useCallback(() => {
    const isNotAdmin = String(clientId) !== '0';

    if (isNotAdmin) {
      const filterClient = clients.find(
        p => String(p.value) === String(clientId)
      );

      searchRef.current?.setFieldValue('cliente', {
        value: filterClient?.value,
        label: filterClient?.label,
      });
    }

    setDisableInput(isNotAdmin);
  }, [clientId, clients]);

  const handleSearchBoxSubmit = useCallback(
    async (data: ISearchParam) => {
      if (data.systems === '') {
        enqueueSnackbar(traductorMsg('screens.profile.msgNoProfile'), {
          variant: 'warning',
        });

        return;
      }

      if (String(data.client) === '' && disableInput === false) {
        enqueueSnackbar('Selecione um cliente!', {
          variant: 'warning',
        });

        return;
      }

      setParamsData(data);

      setIsShowingTable(false);
      setIsLoading(true);

      await searchBoxRequest(clientId, codigoPerfil, data)
        .then(res => {
          if (!res.data.length) {
            setProfilePermissions([]);
            enqueueSnackbar(traductorMsg('screens.profile.noRecordes'), {
              variant: 'warning',
            });
            return;
          }

          setProfilePermissions(res.data);
          setIsShowingTable(true);
        })
        .catch(err => {
          setIsShowingTable(false);
          enqueueSnackbar(
            `${traductorMsg('screens.user.error')}: ${err.message}`,
            {
              variant: 'error',
            }
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [disableInput, clientId, codigoPerfil, enqueueSnackbar]
  );

  useEffect(() => {
    getOptions();
  }, [getOptions]);

  useEffect(() => {
    handleGetSystemSelected();
  }, [handleGetSystemSelected]);

  return (
    <>
      <FixedHeader
        title={t('screens.profile.title')}
        subTitle={String(t('screens.profileTable.permissions'))}
      />
      <Fade in timeout={1000} unmountOnExit>
        <PageContainer>
          <Search>
            <SearchBox
              handleSubmit={(data: ISearchParam) => handleSearchBoxSubmit(data)}
              searchBoxRef={searchRef}
              inputs={[
                {
                  name: 'systems',
                  label: String(t('screens.profile.titleEdit')),
                  type: 'select',
                  options: systems,
                  isRequired: true,
                  disabled: isLoading,
                  onChangeEvent: e => getSystemSelected(e),
                  placeholder: String(t('screens.profile.titleEdit')),
                  xl: 6,
                  lg: 6,
                  xs: 12,
                  md: 6,
                  sm: 6,
                },
                {
                  name: 'client',
                  label: String(t('screens.profile.client')),
                  type: 'select',
                  options: clients,
                  isRequired: true,
                  isDisabled: disableInput,
                  placeholder: String(t('screens.profile.client')),
                  xl: 6,
                  lg: 6,
                  xs: 12,
                  md: 6,
                  sm: 6,
                },
              ]}
              searchDisabled={isLoading}
            />
          </Search>
          {isLoading && <LoaderPda />}
        </PageContainer>
      </Fade>
      {!isLoading && isShowingTable && (
        <ProfilePermissionsTable
          rows={profilePermissions}
          paramsData={paramsData}
          isAdmin={disableInput}
        />
      )}
    </>
  );
};

export default ProfilePermissions;
